import React, { useState, useRef } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase/client';
import { uploadSatelliteImage, uploadOriginalSatelliteImage } from '../../utils/firebase/storage';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import SatelliteViewCapture, { CaptureMetadata } from './SatelliteViewCapture';
import SatelliteAnnotator from './SatelliteAnnotator';

interface SatelliteViewManagerProps {
  wellId: string;
  wellName: string;
  latitude: number;
  longitude: number;
  companyId: string;
  onComplete: (satelliteViewData: SatelliteViewData) => void;
  onCancel: () => void;
}

export interface SatelliteViewData {
  imageUrl: string;
  downloadUrl: string;
  metadata: CaptureMetadata;
  hasAnnotations: boolean;
  createdAt: Date;
}

type Step = 'capture' | 'annotate' | 'saving' | 'success';

export default function SatelliteViewManager({
  wellId,
  wellName,
  latitude,
  longitude,
  companyId,
  onComplete,
  onCancel
}: SatelliteViewManagerProps) {
  const [step, setStep] = useState<Step>('capture');
  const [capturedImage, setCapturedImage] = useState('');
  const [captureMetadata, setCaptureMetadata] = useState<CaptureMetadata | null>(null);
  const [savingMessage, setSavingMessage] = useState('Preparing to save...');
  
  // Use ref to persist image data across renders
  const imageDataRef = useRef<string>('');

  const handleCapture = async (imageData: string, metadata: CaptureMetadata) => {
    
    // Store metadata
    setCaptureMetadata(metadata);
    
    // Store image immediately for annotation
    setCapturedImage(imageData);
    imageDataRef.current = imageData;
    setStep('annotate');
    
    // Upload immediately as requested
    try {
      toast.loading('Uploading capture...');
      await uploadSatelliteImage(
        imageData,
        wellId,
        companyId,
        {
          hasAnnotations: 'false',
          zoom: metadata.zoom.toString(),
          capturedAt: metadata.capturedAt.toISOString()
        }
      );
      toast.dismiss();
      toast.success('Image captured and uploaded!');
    } catch (error) {
      console.error('❌ Failed to upload capture:', error);
      toast.dismiss();
      toast.error('Failed to upload capture');
    }
  };

  const handleAnnotationSave = async (annotatedImageData: string, hasAnnotations: boolean) => {
    if (!captureMetadata) {
      console.error('❌ No capture metadata available');
      toast.error('Missing capture metadata');
      return;
    }

    // Use the ref as fallback if state was lost
    const imageToSave = annotatedImageData || imageDataRef.current;
    
    if (!imageToSave || imageToSave.length < 100) {
      console.error('❌ Invalid annotated image data');
      toast.error('Invalid image data');
      return;
    }


    setStep('saving');
    setSavingMessage('Compressing image...');

    try {
      // STEP 1: Upload the annotated satellite image to Firebase Storage
      // This will OVERWRITE any existing active.jpg file at this path
      setSavingMessage('Uploading to cloud storage...');
      
      const { imageUrl, downloadUrl, size } = await uploadSatelliteImage(
        imageToSave,
        wellId,
        companyId,
        {
          hasAnnotations: hasAnnotations.toString(),
          zoom: captureMetadata.zoom.toString(),
          capturedAt: captureMetadata.capturedAt.toISOString()
        }
      );


      // REMOVED: updateDoc call - we don't update the well document here anymore
      // The well document might not exist yet (for new wells)
      // The data is passed back to the parent form via onComplete
      
      const satelliteViewData: SatelliteViewData = {
        imageUrl,
        downloadUrl,
        metadata: captureMetadata,
        hasAnnotations,
        createdAt: new Date()
      };

      setStep('success');
      
      // Wait a moment to show success message before closing
      setTimeout(() => {
        onComplete(satelliteViewData);
      }, 1500);
    } catch (error) {
      console.error('❌ Error saving satellite view:', error);
      toast.error(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setStep('annotate'); // Go back to annotate step
    }
  };

  // Success modal
  if (step === 'success') {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center backdrop-blur-sm">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl animate-in fade-in zoom-in duration-300">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Saved Successfully!</h3>
            <p className="text-gray-600">Returning to well details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Loading modal during save
  if (step === 'saving') {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center backdrop-blur-sm">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="relative">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-blue-600"></div>
              <Loader2 className="absolute inset-0 m-auto h-10 w-10 text-blue-600 animate-pulse" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Saving Satellite View</h3>
            <p className="text-gray-600">{savingMessage}</p>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-green-500 h-full animate-pulse" style={{ width: '75%' }}></div>
            </div>
            <p className="text-xs text-gray-500">Please wait, this may take a few moments...</p>
          </div>
        </div>
      </div>
    );
  }

  // Annotation step
  if (step === 'annotate') {
    const imageForAnnotation = capturedImage || imageDataRef.current;
    
    if (!imageForAnnotation || !captureMetadata) {
      console.error('❌ Missing image data or metadata for annotation');
      toast.error('Image data was lost. Please capture again.');
      setStep('capture');
      return null;
    }
    
    return (
      <SatelliteAnnotator
        imageData={imageForAnnotation}
        metadata={captureMetadata}
        wellName={wellName}
        onSave={handleAnnotationSave}
        onCancel={() => setStep('capture')}
      />
    );
  }

  // Capture step (default)
  return (
    <SatelliteViewCapture
      wellName={wellName}
      latitude={latitude}
      longitude={longitude}
      onCapture={handleCapture}
      onCancel={onCancel}
    />
  );
}
